import {
  PlayCircle,
  UserCheck,
  HelpCircle,
  GraduationCap,
  BadgeCheck,
  ClipboardCheck,
  ShieldAlert,
  MessagesSquare,
  PhoneCall,
} from 'lucide-react';
import { INTERIM_EVENT_TYPE } from './interimEventTypes.js';

/**
 * Registry of Work Queue event types (aims-htl V1).
 *
 * The 7 V1 event types (CT-4859..CT-4865) plus the two Client event types shown
 * in the prototype. Each per-type detail body is registered by its own story;
 * unregistered types fall back to the generic body (CT-4856).
 */
export const EVENT_TYPE = {
  HTL_CONTINUATION: 'htl_continuation',
  HTL_HANDOFF: 'htl_handoff',
  QUESTION: 'question',
  TRAIN_ME: 'train_me',
  GOV_PROMOTION: 'gov_promotion',
  GOV_REVIEW: 'gov_review',
  GOV_BREAK_GLASS: 'gov_break_glass',
  CLIENT_CONTINUATION: 'client_continuation',
  CLIENT_HANDOFF: 'client_handoff',
};

/**
 * Event types that can carry a workflow trace, i.e. the ones the prototype ever
 * offers Trace on. Walked row by row with Playwright (26 rows, both views): the
 * 8 events with a `sourceWorkflow` map to exactly these 6 categories —
 * `gov-promotion` (EVT-001), `htl-continuation` (EVT-002), `gov-review`
 * (EVT-007), `htl-handoff` (EVT-021), `client-continuation` (EVT-C001/C002)
 * and `client-handoff` (EVT-C003/C004).
 *
 * Never offered on: Question, Train Me, Break Glass, Gov Change Request.
 *
 * This is a ceiling, not the rule. The prototype gates per EVENT on
 * `event.sourceWorkflow` — EVT-006 is also `gov-review` and has no trace, so two
 * rows of the same type can differ. Its empty state says why: "This event was
 * triggered directly, not via an automated workflow pipeline." So callers must
 * check the data too ({@link eventHasWorkflowTrace}).
 */
export const TRACE_EVENT_TYPES = new Set([
  EVENT_TYPE.HTL_CONTINUATION,
  EVENT_TYPE.HTL_HANDOFF,
  EVENT_TYPE.GOV_PROMOTION,
  EVENT_TYPE.GOV_REVIEW,
  EVENT_TYPE.CLIENT_CONTINUATION,
  EVENT_TYPE.CLIENT_HANDOFF,
]);

/** Event types that do not show the Attestation tab (no governance attestation). */
export const HIDE_ATTESTATION_TYPES = new Set([
  EVENT_TYPE.QUESTION,
  EVENT_TYPE.TRAIN_ME,
  INTERIM_EVENT_TYPE.CLAIM_SUGGESTION,
]);

/**
 * Extra type chip on inbox rows (beyond the severity chip).
 * No `proposal`/`task` entries here either — same reasoning as the missing
 * `EVENT_TYPE_META` entries above; this map is unreachable for them anyway
 * (`InboxListItem.jsx` requires both a chip class AND a meta to render the
 * chip), kept absent for the same TODO(product) reason instead of dead code
 * that only works by accident.
 */
export const INBOX_TYPE_CHIP_CLASS = {
  [EVENT_TYPE.QUESTION]: 'bg-amber-500/15 text-amber-500',
  [EVENT_TYPE.TRAIN_ME]: 'bg-sky-500/15 text-sky-500',
  [EVENT_TYPE.GOV_REVIEW]: 'bg-violet-500/15 text-violet-400',
};

/**
 * Interim GS wire types → prototype Event Type bucket (filters, chips, icons only).
 * Routing and decision footers still use the wire `type` — aliases do NOT swap
 * interaction models:
 *
 *   claim_suggestion → train_me     · Train Me UI + promote/reject
 *   change_request   → gov_review   · chip/filter bucket only; footer stays
 *                                   Accept / Propose correction / Reject / Ask
 *                                   (GS change-request/resolve). True EVT-006
 *                                   (Submit review + multi-claim) is EVENT_TYPE.GOV_REVIEW.
 */
export const EVENT_TYPE_DISPLAY_ALIAS = {
  [INTERIM_EVENT_TYPE.CLAIM_SUGGESTION]: EVENT_TYPE.TRAIN_ME,
  [INTERIM_EVENT_TYPE.CHANGE_REQUEST]: EVENT_TYPE.GOV_REVIEW,
};

/** Prototype-facing type for filters/chips (wire `event.type` is unchanged). */
export function displayEventType(wireType) {
  return EVENT_TYPE_DISPLAY_ALIAS[wireType] ?? wireType;
}

/** Label + icon for a wire or prototype type (prefer alias target). */
export function getEventTypeMeta(wireType) {
  const display = displayEventType(wireType);
  return EVENT_TYPE_META[display] ?? EVENT_TYPE_META[wireType] ?? null;
}

export function getInboxTypeChipClass(wireType) {
  const display = displayEventType(wireType);
  return INBOX_TYPE_CHIP_CLASS[display] ?? INBOX_TYPE_CHIP_CLASS[wireType] ?? null;
}

export const EVENT_TYPE_META = {
  [EVENT_TYPE.HTL_CONTINUATION]: { id: EVENT_TYPE.HTL_CONTINUATION, labelKey: 'eventType.htlContinuation', icon: PlayCircle },
  [EVENT_TYPE.HTL_HANDOFF]: { id: EVENT_TYPE.HTL_HANDOFF, labelKey: 'eventType.htlHandoff', icon: UserCheck },
  [EVENT_TYPE.QUESTION]: { id: EVENT_TYPE.QUESTION, labelKey: 'eventType.question', icon: HelpCircle },
  [EVENT_TYPE.TRAIN_ME]: { id: EVENT_TYPE.TRAIN_ME, labelKey: 'eventType.trainMe', icon: GraduationCap },
  [EVENT_TYPE.GOV_PROMOTION]: { id: EVENT_TYPE.GOV_PROMOTION, labelKey: 'eventType.govPromotion', icon: BadgeCheck },
  [EVENT_TYPE.GOV_REVIEW]: { id: EVENT_TYPE.GOV_REVIEW, labelKey: 'eventType.govReview', icon: ClipboardCheck },
  [EVENT_TYPE.GOV_BREAK_GLASS]: { id: EVENT_TYPE.GOV_BREAK_GLASS, labelKey: 'eventType.govBreakGlass', icon: ShieldAlert },
  [EVENT_TYPE.CLIENT_CONTINUATION]: { id: EVENT_TYPE.CLIENT_CONTINUATION, labelKey: 'eventType.clientContinuation', icon: MessagesSquare },
  [EVENT_TYPE.CLIENT_HANDOFF]: { id: EVENT_TYPE.CLIENT_HANDOFF, labelKey: 'eventType.clientHandoff', icon: PhoneCall },
  // `proposal` and `task` are deliberately NOT here. Both are real interim GS
  // wire types (CT-5091+) with real data flowing through them, but neither is
  // one of the prototype's 8 entity types — the prototype never defines a
  // "Proposal" or "Task" chip/icon, so giving them one here would show a type
  // identity the design never designed for.
  //
  // TODO(product): if/when product asks for these to have a real visual
  // identity in the inbox (chip, icon, tooltip, and an Event Type filter
  // entry), add them back here + to INBOX_TYPE_CHIP_CLASS + remove them from
  // TYPE_FILTER_EXCLUDED_WIRE_TYPES in filterOptions.js — all three gate off
  // the same absence today, on purpose, so re-adding is a 3-file change, not
  // a search-and-hope.
};

/** Category shorthand shown as a small chip on cards (GOV / AGNT / CLIENT). */
export const CATEGORY = {
  GOV: 'GOV',
  AGNT: 'AGNT',
  CLIENT: 'CLIENT',
};
